// src/api/AxiosAdmin.ts

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

// Base URL setup (same as user client)
const baseURL = (process.env.REACT_APP_API_BASE_URL as string)?.replace(/\/+$/, "");

if (!baseURL) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME || "csrf_token";
const CSRF_HEADER_NAME = "x-csrf-token";

const adminApi: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

// ---------------- Access token (in-memory, isolated) ----------------
let adminAccessToken: string | null = null;

export function setAdminAccessToken(token: string | null) {
  adminAccessToken = token;
}
export function getAdminAccessToken() {
  return adminAccessToken;
}

// ---------------- CSRF handling (shared logic) ----------------
let csrfTokenMem: string | null = null;
let csrfInFlight: Promise<string | null> | null = null;

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const m = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function isUnsafeMethod(method?: string) {
  const m = (method || "GET").toUpperCase();
  return m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE";
}

export async function ensureAdminCsrf(): Promise<string | null> {
  if (csrfTokenMem) return csrfTokenMem;

  if (!csrfInFlight) {
    csrfInFlight = (async () => {
      try {
        const res = await adminApi.get("/auth/csrf", { skipAuthRefresh: true } as any);
        const token = (res.data as any)?.token || getCookie(CSRF_COOKIE_NAME);
        csrfTokenMem = token || null;
        return csrfTokenMem;
      } catch {
        csrfTokenMem = getCookie(CSRF_COOKIE_NAME);
        return csrfTokenMem;
      } finally {
        csrfInFlight = null;
      }
    })();
  }

  return csrfInFlight;
}

// ---------------- Request interceptor ----------------
adminApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers = config.headers || {};

  if (adminAccessToken) {
    config.headers.Authorization = `Bearer ${adminAccessToken}`;
  }

  if (isUnsafeMethod(config.method)) {
    const csrf = csrfTokenMem || getCookie(CSRF_COOKIE_NAME);
    if (csrf) {
      (config.headers as any)[CSRF_HEADER_NAME] = csrf;
    }
  }

  const isForm = typeof FormData !== "undefined" && config.data instanceof FormData;
  if (isForm) {
    delete (config.headers as any)["Content-Type"];
    delete (config.headers as any)["content-type"];
  }

  return config;
});

// ---------------- Refresh logic ----------------
let refreshInFlight: Promise<string> | null = null;

async function doAdminRefreshOnce(): Promise<string> {
  const csrf = await ensureAdminCsrf();

  try {
    const res = await adminApi.post("/admin/auth/refresh", {}, { skipAuthRefresh: true } as any);
    const newToken = (res.data as any)?.accessToken;
    if (!newToken) throw new Error("No accessToken returned from admin refresh");

    setAdminAccessToken(newToken);
    return newToken;
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 403) {
      csrfTokenMem = null;
      await ensureAdminCsrf();
      const retryRes = await adminApi.post("/admin/auth/refresh", {}, { skipAuthRefresh: true } as any);
      const newToken = (retryRes.data as any)?.accessToken;
      if (!newToken) throw new Error("No accessToken returned from admin refresh (retry)");
      setAdminAccessToken(newToken);
      return newToken;
    }
    throw e;
  }
}

export async function refreshAdminAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = doAdminRefreshOnce().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ---------------- Response interceptor ----------------
adminApi.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<any>) => {
    const status = error.response?.status;
    const original: any = error.config;

    if (!original || original._retry) throw error;

    const isRefreshCall =
      original.url?.includes("/auth/refresh") || (original as any).skipAuthRefresh;

    if (status === 401 && !isRefreshCall) {
      original._retry = true;

      try {
        const newToken = await refreshAdminAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return adminApi(original);
      } catch (refreshErr) {
        setAdminAccessToken(null);
        throw refreshErr;
      }
    }

    throw error;
  }
);

export default adminApi;
